import { Col, Form, FormInstance, Input, Modal, Row, Table } from 'antd';
import { UploadChangeParam } from 'antd/es/upload';
import React, { useEffect, useState } from 'react';

import AnchorComponent from 'components/atoms/AnchorComponent';
import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import InputNumberComponent from 'components/atoms/InputNumberComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import UploadComponent from 'components/atoms/UploadComponent';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import TranslateText from 'components/hoc/TranslateText';

import {
    IDodatekDFP,
    IDodatekDFPUcet,
    IDodatekDFPUcetPut,
    dokumentUcetDfpDelete,
    dokumentUcetDfpDeleteAll,
    dokumentUcetDfpGet,
    dokumentUcetDfpPost,
    dokumentUcetDfpPut
} from 'services/dokumenty/dodatek';
import { IKoducastnika, ueKoducastnika } from 'services/specialniSluzby/ueKoducastnika';
import { IStavOvereni } from 'services/specialniSluzby/ueZalozeni';

import { pocetCislic } from 'enums/regExp';

import trueFalseSelect from 'data/codebooks/trueFalseSelect';

import { validateICO } from 'utils/validations/icoValidation';
import { validateNID } from 'utils/validations/nidValidation';
import numberValidation from 'utils/validations/numberValidation';
import { validateRdc } from 'utils/validations/rodneCisloValidation';

import FormWrapper from '../FormWrapper';
import InfoTooltip from '../InfoTooltip';

export const DfpTable = (
    zadostID: number,
    onDetailDisplay: () => void,
    outForm: FormInstance<any>,
    required: boolean,
    type?: 'znizeni' | 'vytesneni' | 'dodatek' | 'seznam',
    showPPN?: boolean,
    signingDisabled?: boolean,
    validateFields?: string[]
) => {
    const { confirm } = Modal;
    const [dataUcty, setDataUcty] = useState<IDodatekDFP>();
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [refetchUcty, setRefetchUcty] = useState<boolean>(false);
    const [selectedRow, setSelectedRow] = useState<IDodatekDFPUcet>();
    const [displayMode, setDisplayMode] = useState<'ucet' | 'soubor' | undefined>();
    const [koducastnika, setKoducastnika] = useState<{ value: string; labelcz: string }[]>([]);
    const [modalResp, setModalResp] = useState<string>('');
    const [idvlastnikaICO, setIdvlastnikaICO] = useState<boolean>(false);
    const [idvlastnikaRDC, setIdvlastnikaRDC] = useState<boolean>(false);
    const [idvlastnikaNID, setIdvlastnikaNID] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [, rerender] = useState<boolean>(false);
    const [fileData, setFileData] = useState<UploadChangeParam>();
    // const [fileRespData, setFileRespData] = useState<INacteni>();
    const [fileErr, setFileErr] = useState<string>('');

    const showPPNerr = showPPN && outForm.getFieldValue('ppn') === 'true' && outForm.getFieldValue('rliv');

    useEffect(() => {
        setDisplayMode(selectedRow ? 'ucet' : undefined);
        setIdvlastnikaICO(validateICO(selectedRow?.idmb || '') === 0 ? true : false);
        setIdvlastnikaRDC(validateRdc(selectedRow?.idmb || '') === 0 ? true : false);
        setIdvlastnikaNID(validateNID(selectedRow?.idmb || '') === 0 ? true : false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRow]);

    useEffect(() => {
        ueKoducastnika().then((resp: IKoducastnika) => {
            const tmpSelectArr: { value: string; labelcz: string }[] = [];
            resp.kodyucastnika.forEach(item => {
                item.kod !== '598' &&
                    item.kod !== '802' &&
                    tmpSelectArr.push({
                        value: item.kod,
                        labelcz: item.kod + ' - ' + item.nazev
                    });
            });
            setKoducastnika(tmpSelectArr);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zadostID]);

    useEffect(() => {
        setLoading(true);
        dokumentUcetDfpGet(zadostID)
            .then(resp => {
                setDataUcty(resp);
            })
            .catch(() => setDataUcty({} as IDodatekDFP))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zadostID, refetchUcty]);

    const cols = [
        {
            title: <TranslateText>KOD_UCASTNIKA</TranslateText>,
            dataIndex: 'cdac',
            key: 'cdac'
        },
        {
            title: (
                <>
                    <TranslateText>CISLO_MAJETKOVEHO_UCTU</TranslateText>
                    <InfoTooltip title="DOKUMENT_DODATEK_DFP_TOOLTIP_MUCET1" />
                </>
            ),
            dataIndex: 'rcma',
            key: 'rcma'
        },
        {
            title: (
                <>
                    <TranslateText>ID_OSOBY</TranslateText>
                    <InfoTooltip title="DOKUMENT_DODATEK_DFP_TOOLTIP_NABYVATEL" />
                </>
            ),
            dataIndex: 'idma',
            key: 'idma'
        },
        ...(type !== 'vytesneni'
            ? [
                  {
                      title:
                          type === 'znizeni' ? (
                              <>
                                  <TranslateText>POCET_ZRUSENICH_CP</TranslateText>
                                  <InfoTooltip title="DOKUMENT_DODATEK_DFP_TOOLTIP_CP_ZRUSENI" />
                              </>
                          ) : type === 'dodatek' ? (
                              <>
                                  <TranslateText>POCET_CP</TranslateText>
                                  <InfoTooltip title="DOKUMENT_DODATEK_DFP_TOOLTIP_CP" />
                              </>
                          ) : (
                              <>
                                  <TranslateText>PRIKAZ_CP_PPN</TranslateText>
                                  <InfoTooltip title="DOKUMENT_DODATEK_DFP_TOOLTIP_NABYVATEL" />
                              </>
                          ),
                      dataIndex: 'qtet',
                      key: 'qtet'
                  }
              ]
            : []),

        ...(type === 'dodatek'
            ? [
                  {
                      title: (
                          <>
                              <TranslateText>CISLO_ZASTAVNI_SMLOUVY</TranslateText>
                              <InfoTooltip title="DOKUMENT_DODATEK_DFP_TOOLTIP_ZAST_SMLOUVA" />
                          </>
                      ),
                      dataIndex: 'rliv',
                      key: 'rliv'
                  },
                  {
                      title: <TranslateText>PPN</TranslateText>,
                      dataIndex: 'ppn',
                      key: 'ppn',
                      render: (ppn: boolean) => (
                          <TranslateText>{typeof ppn === 'boolean' ? (ppn ? 'ANO' : 'NE') : ''}</TranslateText>
                      )
                  }
              ]
            : []),
        {
            title: (
                <>
                    <TranslateText>ID_OSOBY_EVIDENCE</TranslateText>
                    <InfoTooltip title="DOKUMENT_DODATEK_DFP_TOOLTIP_MAJITEL" />
                </>
            ),
            dataIndex: 'idmb',
            key: 'idmb'
        },
        {
            title: (
                <>
                    <TranslateText>JMENO</TranslateText> <InfoTooltip title="DOKUMENT_DODATEK_DFP_TOOLTIP_JMENO" />
                </>
            ),
            dataIndex: 'jmma',
            key: 'jmma'
        },
        {
            title: (
                <>
                    <TranslateText>PRIJMENI_OBCHODNI_FIRMA</TranslateText>
                    <InfoTooltip title="DOKUMENT_DODATEK_DFP_TOOLTIP_PRIJMENI" />
                </>
            ),
            dataIndex: 'pjma',
            key: 'pjma'
        },
        {
            dataIndex: 'iducet',
            key: 'iducet',
            render: (_a: string, row: IDodatekDFPUcet) => (
                <AnchorComponent
                    onClick={() => {
                        //   outForm.resetFields(['']);
                        onDetailDisplay();
                        setSelectedRow(row);
                        setIsEdit(false);
                    }}
                >
                    <TranslateText>DETAIL</TranslateText>
                </AnchorComponent>
            )
        }
    ];

    const renderModal = () => {
        return (
            <Modal
                title={<TranslateText>SS_NOVY_RUCNI_MODAL_ZAPIS</TranslateText>}
                open={modalResp !== ''}
                onCancel={() => {
                    setModalResp('');
                }}
                footer={[
                    <Row>
                        <ButtonComponent
                            buttontext="ZAVRIT"
                            onClick={() => {
                                setModalResp('');
                            }}
                            id="zavritModalBtn"
                        />
                    </Row>
                ]}
            >
                <Row>
                    <Paragraph
                        text={
                            modalResp === 'TIMEOUT'
                                ? 'SS_SLUZBA_ERROR_NESKORO'
                                : 'SS_NOVY_RUCNI_MODAL_RESP_' + modalResp
                        }
                    />
                </Row>
            </Modal>
        );
    };

    const DfpTableRender = () => (
        <LoadingWrapper loading={loading}>
            <Table
                rowKey={(_, index) => index || 0}
                locale={{
                    emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                }}
                style={{
                    margin: '30px 0px'
                }}
                columns={cols}
                dataSource={dataUcty?.ucty}
                pagination={false}
                rowClassName={(_a, i) => {
                    const classes = [];
                    if (i % 2) classes.push('tableRowGray');
                    return classes.join(' ');
                }}
            />
            {dataUcty && type !== 'vytesneni' && (
                <Row
                    style={{
                        marginTop: 10
                    }}
                >
                    <Col>
                        <Title
                            level={5}
                            text={
                                type === 'znizeni'
                                    ? 'PRIKAZ_ZNIZENI_DFP_POCETCP'
                                    : type === 'dodatek'
                                      ? 'DOKUMENT_DODATEK_DFP_POCETCP'
                                      : 'DOKUMENT_DODATEK_POCETCP'
                            }
                            style={{
                                marginRight: 8
                            }}
                        />
                    </Col>
                    <Col span={4}>
                        <Input disabled={true} value={dataUcty?.celkemcp} />
                    </Col>
                </Row>
            )}
            {!signingDisabled && (
                <Form.Item
                    style={{
                        marginBottom: 34
                    }}
                    name="pozadavky"
                    rules={[
                        () => ({
                            validator() {
                                if (dataUcty?.ucty?.length || !required) return Promise.resolve();
                                return Promise.reject(<TranslateText>DFP_UCET_ERR</TranslateText>);
                            }
                        })
                    ]}
                >
                    <Row className="button-row">
                        <ButtonComponent
                            id="pridatPozadavekBtn"
                            buttontext={
                                type === 'seznam' || type === 'vytesneni' ? 'PRIDAT_UCET' : 'SS_PRIDAT_POZADAVEK'
                            }
                            disabled={type === 'vytesneni' && !!dataUcty?.ucty?.length}
                            onClick={() => {
                                const action = () => {
                                    onDetailDisplay();
                                    setIsEdit(true);
                                    setTimeout(
                                        () =>
                                            outForm.setFieldsValue({
                                                cdac: undefined,
                                                rcma: undefined,
                                                idma: undefined,
                                                qtet: undefined,
                                                idmb: undefined,
                                                pjma: undefined,
                                                jmma: undefined,
                                                ppn: undefined,
                                                rliv: undefined
                                            }),
                                        100
                                    );
                                    setSelectedRow({} as IDodatekDFPUcet);
                                };
                                if (validateFields?.length) {
                                    outForm.validateFields(validateFields).then(() => action());
                                } else {
                                    action();
                                }
                            }}
                        />

                        {
                            // <ButtonComponent
                            //    id="pridatPozadavkeSouborBtn"
                            //   buttontext="SS_PRIDAT_ZE_SOUBORU"
                            // onClick={() => setDisplayMode('soubor')}
                            // />
                            // <ButtonComponent
                            // id="csvDownloadbtn"
                            // buttontext="VYSTUP_CSV"
                            // onClick={() => {
                            //    setLoading(true);
                            //    ueSouborVystupCSV(
                            //        specialniSluzby?.detail?.id!,
                            //        dataFilterActive ? outForm.getFieldsValue() : null
                            //    )
                            //        .then(file => {
                            //            if (file.redirect) {
                            //                fileDownload(file.redirect).finally(() => setLoading(false));
                            //            }
                            //        })
                            //
                            //        .finally(() => {
                            //            setLoading(false);
                            //        });
                            // }}
                            // />
                        }
                        <ButtonComponent
                            id="smazatBtn"
                            buttontext="SMAZAT"
                            onClick={() =>
                                confirm({
                                    title: <TranslateText>DOKUMENT_DODATEK_DFP_DEL_CONFIRM_ALL</TranslateText>,
                                    cancelText: <TranslateText>NE</TranslateText>,
                                    okText: <TranslateText>ANO</TranslateText>,
                                    onOk() {
                                        setLoading(true);
                                        dokumentUcetDfpDeleteAll(zadostID)
                                            .then(() => {
                                                setRefetchUcty(pv => !pv);
                                            })
                                            .finally(() => setLoading(false));
                                    }
                                })
                            }
                        />
                    </Row>
                </Form.Item>
            )}
        </LoadingWrapper>
    );

    const DfpUcetRender = () => (
        <LoadingWrapper loading={loading}>
            <FormWrapper
                outForm={outForm}
                onFinish={value => {
                    const valsToSend = {
                        ...value,
                        ...(value.ppn && {
                            ppn: value.ppn === 'true'
                        })
                    } as IDodatekDFPUcetPut;
                    setLoading(true);
                    (selectedRow?.iducet
                        ? dokumentUcetDfpPut(zadostID, selectedRow!.iducet, valsToSend)
                        : dokumentUcetDfpPost(zadostID, valsToSend)
                    )
                        .then(resp => {
                            if (resp.stavovereni === IStavOvereni.NENALEZENO && resp.stav === 'ODMITNUTO')
                                setModalResp('UCET');
                            if (resp.stavovereni === IStavOvereni.VLASTNIK && resp.stav === 'ODMITNUTO')
                                setModalResp('VLASTNIK');
                            else if (resp.sankce) setModalResp('SANKCE');
                            else if (resp.stav === 'OK') {
                                setSelectedRow(undefined);
                                setRefetchUcty(pv => !pv);
                            } else setModalResp(resp.stav);
                        })
                        .finally(() => setLoading(false));
                }}
            >
                <SelectComponent
                    isFormItem={true}
                    data={koducastnika || []}
                    name="cdac"
                    defaultValue={selectedRow?.cdac}
                    labelParagraph={'KOD_UCASTNIKA'}
                    disabled={!isEdit}
                />
                <InputComponent
                    name="rcma"
                    label="CISLO_MAJETKOVEHO_UCTU"
                    initialValue={selectedRow?.rcma}
                    maxLength={12}
                    rules={[
                        () => ({
                            validator(_, value: string) {
                                if (!value) return Promise.resolve();
                                if (/^(100|808|909)\d{9}$/.test(value)) return Promise.resolve();
                                return Promise.reject(<TranslateText>SS_NOVY_RUCNI_VALIDACE_UCET</TranslateText>);
                            }
                        })
                    ]}
                    disabled={!isEdit}
                />
                <InputComponent
                    name="idma"
                    label="ID_OSOBY"
                    initialValue={selectedRow?.idma}
                    maxLength={10}
                    rules={[
                        {
                            validator(_rule, value: string) {
                                if (!value) return Promise.resolve();
                                if (pocetCislic(8, 10).test(value)) return Promise.resolve();
                                return Promise.reject(
                                    <TranslateText>SS_NOVY_RUCNI_VALIDACE_IDNABYVATELE_CISLA</TranslateText>
                                );
                            }
                        }
                    ]}
                    disabled={!isEdit}
                />
                {type !== 'vytesneni' && (
                    <InputNumberComponent
                        name="qtet"
                        label="POCET_CP"
                        initialValue={selectedRow?.qtet}
                        maxLength={12}
                        precision={0}
                        disabled={!isEdit}
                        rules={numberValidation(true)}
                    />
                )}
                {type === 'dodatek' && (
                    <>
                        <InputComponent
                            name="rliv"
                            label="CISLO_ZASTAVNI_SMLOUVY"
                            initialValue={selectedRow?.rliv}
                            maxLength={12}
                            rules={[
                                () => ({
                                    validator(_, value: string) {
                                        if (!value) return Promise.resolve();
                                        if (/^6\d{11}$/.test(value)) return Promise.resolve();
                                        return Promise.reject(
                                            <TranslateText>SS_NOVY_RUCNI_VALIDACE_ZASTAVNI</TranslateText>
                                        );
                                    }
                                })
                            ]}
                            notRequired={true}
                            disabled={!isEdit || outForm.getFieldValue('ppn') === 'true'}
                            onChange={() => rerender(pv => !pv)}
                            onBlur={() =>
                                outForm.getFieldValue('rliv') &&
                                outForm.setFieldsValue({
                                    ppn: 'false'
                                })
                            }
                        />
                        {showPPN && (
                            <SelectComponent
                                isFormItem={true}
                                data={trueFalseSelect}
                                name="ppn"
                                defaultValue={
                                    typeof selectedRow?.ppn === 'boolean' ? String(selectedRow?.ppn) : undefined
                                }
                                labelParagraph={'PPN_SELECT'}
                                disabled={!isEdit || !!outForm.getFieldValue('rliv')}
                                onChange={val => {
                                    val === 'true' &&
                                        outForm.setFieldsValue({
                                            rliv: undefined
                                        });
                                    rerender(pv => !pv);
                                }}
                            />
                        )}
                    </>
                )}
                <Paragraph
                    text="SS_NOVY_RUCNI_UVEDTE"
                    style={{
                        display: 'block'
                    }}
                />
                <InputComponent
                    name="idmb"
                    label="SS_NOVY_RUCNI_ID_C_VLASTNIKA"
                    initialValue={selectedRow?.idmb}
                    maxLength={10}
                    notRequired={true}
                    rules={[
                        () => ({
                            validator(_, value: string) {
                                if (!value) return Promise.resolve();
                                if (validateICO(value) === 0 || validateRdc(value) === 0 || validateNID(value) === 0)
                                    return Promise.resolve();
                                return Promise.reject(<TranslateText>UPOZORNENI_NEVALIDNIRC</TranslateText>);
                            }
                        })
                    ]}
                    onBlur={e => {
                        const value = e.target.value;
                        setIdvlastnikaICO(validateICO(value) === 0 ? true : false);
                        setIdvlastnikaRDC(validateRdc(value) === 0 ? true : false);
                        setIdvlastnikaNID(validateNID(value) === 0 ? true : false);
                        outForm.setFields([
                            {
                                name: 'pjma',
                                errors: []
                            },
                            {
                                name: 'jmma',
                                errors: []
                            }
                        ]);
                    }}
                    disabled={!isEdit}
                />
                <InputComponent
                    name="pjma"
                    label="SS_NOVY_RUCNI_PRIJMENI"
                    initialValue={selectedRow?.pjma}
                    maxLength={80}
                    notRequired={!(idvlastnikaICO || idvlastnikaRDC || idvlastnikaNID)}
                    rules={[
                        {
                            max: 80,
                            message: <TranslateText>NEVALIDNI_POCET_ZNAKU</TranslateText>
                        }
                    ]}
                    disabled={!isEdit}
                />
                <InputComponent
                    name="jmma"
                    label="SS_NOVY_RUCNI_JMENO"
                    initialValue={selectedRow?.jmma}
                    maxLength={80}
                    notRequired={!idvlastnikaRDC}
                    rules={[
                        {
                            max: 80,
                            message: <TranslateText>NEVALIDNI_POCET_ZNAKU</TranslateText>
                        }
                    ]}
                    disabled={!isEdit}
                />
                <Row className="button-row">
                    {showPPNerr && isEdit && <Paragraph text={'PPN ERROR'} className="extramessage-text" />}
                    <ButtonComponent
                        buttontext="ZPET"
                        onClick={() => setSelectedRow(undefined)}
                        id="isinZpetBtn"
                        type="default"
                    />
                    {isEdit ? (
                        <ButtonComponent buttontext="POKRACOVAT" htmlType="submit" id="ucetPokracovatBtn" />
                    ) : (
                        <>
                            {!signingDisabled && (
                                <>
                                    <ButtonComponent
                                        buttontext="SS_DETAIL_VYBRANY_SMAZAT"
                                        onClick={() =>
                                            confirm({
                                                title: <TranslateText>DOKUMENT_DODATEK_DFP_DEL_CONFIRM</TranslateText>,
                                                cancelText: <TranslateText>NE</TranslateText>,
                                                okText: <TranslateText>ANO</TranslateText>,
                                                onOk() {
                                                    setLoading(true);
                                                    dokumentUcetDfpDelete(zadostID, selectedRow!.iducet)
                                                        .then(() => {
                                                            setRefetchUcty(pv => !pv);
                                                            setSelectedRow(undefined);
                                                        })
                                                        .finally(() => setLoading(false));
                                                }
                                            })
                                        }
                                        id="isinPokracovatBtn"
                                    />
                                    <ButtonComponent
                                        buttontext="SS_DETAIL_VYBRANY_ZMENIT"
                                        htmlType="submit"
                                        id="isinPokracovatBtn"
                                        onClick={() => {
                                            setIsEdit(true);
                                            outForm.setFieldsValue({
                                                selectedRow
                                            });
                                        }}
                                    />
                                </>
                            )}
                        </>
                    )}
                </Row>
                {renderModal()}
            </FormWrapper>
        </LoadingWrapper>
    );

    const SouborRender = () => (
        <>
            <Row>
                <span
                    style={{
                        paddingRight: '20px'
                    }}
                >
                    <TranslateText>SS_NOVY_SOUBOR_NAZEV_SOUBORU</TranslateText>
                </span>
                <UploadComponent
                    id="uploadBtn"
                    showDownloadIcon={false}
                    onChange={(uplData: UploadChangeParam) => {
                        if (uplData.fileList.length) {
                            uplData?.file?.response && setFileErr('');
                            setFileData(uplData);
                        }
                    }}
                    fileList={(fileData?.fileList || []).map(e => {
                        return {
                            uid: e.uid,
                            name: e.name.normalize(),
                            fileName: e.fileName ? e.fileName : e.response ? e.response.tempnazev || '' : '',
                            type: e.type!,
                            size: e.size!,
                            status: e.status ? e.status : 'error'
                        };
                    })}
                    onRemove={() => {
                        setFileErr('');
                        setFileData(undefined);
                    }}
                    accept=".csv"
                    extramessage={fileErr}
                    buttonDisabled={fileData && fileData.fileList.length > 0 ? true : false}
                />
            </Row>
            <Row className="button-row">
                <ButtonComponent
                    type="default"
                    id="zpetUploadBtn"
                    buttontext="ZPET"
                    onClick={() => {
                        setDisplayMode(undefined);
                    }}
                />
                <ButtonComponent
                    id="uploadSubmitBtn"
                    buttontext="POKRACOVAT"
                    disabled={!fileData?.file.response?.tempnazev}
                    onClick={() => {
                        //   if (!fileData?.file.response?.tempnazev) {
                        //       setFileErr('SOUBOR_POVINNY');
                        //   } else if (specialniSluzby && fileData && fileData.file.response?.tempnazev) {
                        //       setLoading(true);
                        //       ueSouborNacteni(
                        //           specialniSluzby?.detail?.id!,
                        //           {
                        //               file: fileData.file.response?.tempnazev,
                        //               nazev: fileData.file.name || ''
                        //           },
                        //           isDLT
                        //       )
                        //           .then((resp: INacteni) => {
                        //               setFileRespData(resp);
                        //           })
                        //           .catch((err: any) => {
                        //               err.response?.data?.message &&
                        //                   setFileErr(
                        //                       err.response.data.message.includes('Duplicitni')
                        //                           ? 'CHYBA_SOUBOR_DUPLICITNI'
                        //                           : 'CHYBA_SOUBOR_NEVALIDNI'
                        //                   );
                        //           })
                        //           .finally(() => setLoading(false));
                        //   }
                    }}
                />
            </Row>
        </>
    );

    return [DfpTableRender, DfpUcetRender, SouborRender, displayMode, dataUcty] as const;
};
